<template>
  <div class="as-coach-inspect-checklist-card">
    <header class="as-coach-inspect-checklist-card__header">
      <div class="as-coach-inspect-checklist-card__page-header">
        <div
          class="as-coach-inspect-checklist-card__top-row"
          :class="{ 'as-coach-inspect-checklist-card__top-row--no-breadcrumbs': !showBreadcrumbs }"
        >
          <ExBreadcrumbs
            v-if="showBreadcrumbs"
            class="as-coach-inspect-checklist-card__breadcrumbs"
            :breadcrumbsList="breadcrumbs"
            @onClickBreadcrumb="onBreadcrumbClick"
          />
          <EmployeeAssignmentsBadge
            class="as-coach-inspect-checklist-card__assignments-badge"
            :primary-position-name="primaryPositionName"
            :preparing-for-position-name="preparingForPositionName"
          />
          <slot name="top-actions" />
        </div>

        <div class="as-coach-inspect-checklist-card__slide-out-header">
          <div class="as-coach-inspect-checklist-card__main-row">
            <div class="as-coach-inspect-checklist-card__heading">
              <h2 class="as-coach-inspect-checklist-card__title">Inspect</h2>

              <EXTooltip
                class="as-coach-inspect-checklist-card__employee-tooltip"
                placement="top"
                :delay="300"
                :title="displayName"
                :description="jobTitle"
                :disabled="!isEmployeeTruncated"
              >
                <div ref="employeeRef" class="as-coach-inspect-checklist-card__employee">
                  <ExAvatar
                    :picture="avatar"
                    :firstName="resolvedFirstName"
                    :lastName="resolvedLastName"
                    size="medium"
                    class="as-coach-inspect-checklist-card__avatar"
                  />
                  <div class="as-coach-inspect-checklist-card__employee-name-wrap">
                    <span v-if="displayName" class="as-coach-inspect-checklist-card__employee-name">
                      {{ displayName }}
                    </span>
                  </div>
                  <span v-if="jobTitle" class="as-coach-inspect-checklist-card__employee-title">
                    {{ jobTitle }}
                  </span>
                </div>
              </EXTooltip>

              <span class="as-coach-inspect-checklist-card__assignment-label">on assignment</span>
            </div>

            <EXTooltip
              class="as-coach-inspect-checklist-card__responsibility-tooltip"
              placement="top"
              :delay="300"
              :title="responsibilityTooltipText"
              :disabled="!isResponsibilityTruncated"
            >
              <div
                ref="responsibilityWrapRef"
                class="as-coach-inspect-checklist-card__responsibility-dropdown-wrap"
              >
                <ResponsibilityAssignmentDropDown
                  class="as-coach-inspect-checklist-card__responsibility-dropdown"
                  :responsibilityCode="responsibility.responsibilityCode"
                  :responsibilityName="resolvedResponsibilityName"
                  :responsibilityImportance="responsibility.responsibilityImportance"
                  :roleCode="responsibility.roleCode"
                  :roleName="resolvedRoleName"
                  :roleImportance="responsibility.roleImportance"
                  :positionCode="responsibility.positionCode"
                  :positionName="resolvedPositionName"
                  :positionImportance="responsibility.positionImportance"
                  :positionCommitment="responsibility.positionCommitment"
                  :assignedOn="resolvedAssignedOn"
                  :newDuration="responsibility.newDuration"
                  :show_codes="responsibility.show_codes"
                />
              </div>
            </EXTooltip>
          </div>
        </div>
      </div>
    </header>

    <div class="as-coach-inspect-checklist-card__body">
      <AsCoachChecklistInspectionTable
        :levels="levels"
        :pass-filter="passFilter"
        :search-query="searchQuery"
        :employee-name="employeeName"
        @update:passFilter="emit('update:passFilter', $event)"
        @update:searchQuery="emit('update:searchQuery', $event)"
        @pass-filter-change="emit('pass-filter-change', $event)"
        @search="emit('search', $event)"
        @toggle-level="emit('toggle-level', $event)"
        @badge-click="emit('badge-click', $event)"
        @badge-remove="emit('badge-remove', $event)"
        @expiration-synced="emit('expiration-synced', $event)"
        @badge-chatter="emit('badge-chatter', $event)"
        @action-click="emit('action-click', $event)"
        @secondary-action-click="emit('secondary-action-click', $event)"
        @chatter-click="emit('chatter-click', $event)"
      >
        <template v-if="$slots['toolbar-extra']" #toolbar-extra>
          <slot name="toolbar-extra" />
        </template>
      </AsCoachChecklistInspectionTable>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import ExAvatar from "@components/common/ExAvatar.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import EmployeeAssignmentsBadge from "@components/EmployeeAssignmentsBadge/EmployeeAssignmentsBadge.vue";
import ResponsibilityAssignmentDropDown from "@components/ResponsibilityAssignmentDropDown/ResponsibilityAssignmentDropDown.vue";
import AsCoachChecklistInspectionTable from "@components/AsCoachChecklistInspectionTable/AsCoachChecklistInspectionTable.vue";

const props = defineProps({
  /** Hide the breadcrumb trail (e.g. when the card is hosted in a drawer). */
  showBreadcrumbs: {
    type: Boolean,
    default: true,
  },
  /** Breadcrumb trail for the coach inspection page. */
  breadcrumbs: {
    type: Array,
    default: () => [
      { text: "Responsibilities to Inspect", href: "#" },
      { text: "Employee Inspection Checklist", disabled: true },
    ],
  },
  /** Employee primary position label shown in the assignments badge. */
  primaryPositionName: {
    type: String,
    default: "",
  },
  /** Employee preparing-for position label shown in the assignments badge. */
  preparingForPositionName: {
    type: String,
    default: "",
  },
  /** Employee display name (e.g. "Olivia Rhye"). */
  employeeName: {
    type: String,
    default: "",
  },
  firstName: {
    type: String,
    default: "",
  },
  lastName: {
    type: String,
    default: "",
  },
  /** Employee job title shown next to the name. */
  jobTitle: {
    type: String,
    default: "",
  },
  /** Employee profile photo URL or asset. */
  avatar: {
    type: [String, Object],
    default: null,
  },
  /**
   * Responsibility assignment shown in the header dropdown.
   * Shape matches ResponsibilityAssignmentDropDown props.
   */
  responsibility: {
    type: Object,
    default: () => ({}),
  },
  /** Override responsibility name when not nested on `responsibility`. */
  responsibilityName: {
    type: String,
    default: "",
  },
  /** Override role name when not nested on `responsibility`. */
  roleName: {
    type: String,
    default: "",
  },
  /** Override position name when not nested on `responsibility`. */
  positionName: {
    type: String,
    default: "",
  },
  /** Override assigned-on date when not nested on `responsibility`. */
  assignedOn: {
    type: [Date, String],
    default: null,
  },
  /**
   * Level groups for the checklist table body.
   * Shape matches AsCoachChecklistInspectionTable `levels`.
   */
  levels: {
    type: Array,
    default: () => [],
  },
  /** Active pass filter: `not-yet-passed` | `all`. */
  passFilter: {
    type: String,
    default: "not-yet-passed",
    validator: (value) => ["not-yet-passed", "all"].includes(value),
  },
  /** Controlled search query for the checklist table. */
  searchQuery: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "breadcrumb-click",
  "update:passFilter",
  "update:searchQuery",
  "pass-filter-change",
  "search",
  "toggle-level",
  "badge-click",
  "badge-remove",
  "badge-chatter",
  "expiration-synced",
  "action-click",
  "secondary-action-click",
  "chatter-click",
]);

const resolvedFirstName = computed(() => {
  if (props.firstName) return props.firstName;
  return props.employeeName.split(" ")[0] || "";
});

const resolvedLastName = computed(() => {
  if (props.lastName) return props.lastName;
  const nameParts = props.employeeName.split(" ").filter(Boolean);
  return nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
});

const displayName = computed(() => {
  if (props.employeeName) return props.employeeName;
  return [props.firstName, props.lastName].filter(Boolean).join(" ");
});

const resolvedResponsibilityName = computed(
  () => props.responsibilityName || props.responsibility.responsibilityName || ""
);

const resolvedRoleName = computed(() => props.roleName || props.responsibility.roleName || "");

const resolvedPositionName = computed(
  () => props.positionName || props.responsibility.positionName || ""
);

const resolvedAssignedOn = computed(() => {
  const value = props.assignedOn || props.responsibility.assignedOn;
  if (!value) return undefined;
  return value instanceof Date ? value : new Date(value);
});

const responsibilityTooltipText = computed(() => {
  const code = props.responsibility.show_codes ? props.responsibility.responsibilityCode : "";
  return [code, resolvedResponsibilityName.value].filter(Boolean).join(" ");
});

const employeeRef = ref(null);
const responsibilityWrapRef = ref(null);
const isEmployeeTruncated = ref(false);
const isResponsibilityTruncated = ref(false);
let resizeObserver = null;

function isOverflowing(el) {
  return !!el && el.scrollWidth > el.clientWidth;
}

function measureTruncation() {
  const employee = employeeRef.value;
  isEmployeeTruncated.value =
    !!employee &&
    [
      ".as-coach-inspect-checklist-card__employee-name",
      ".as-coach-inspect-checklist-card__employee-title",
    ].some((selector) => isOverflowing(employee.querySelector(selector)));

  const wrap = responsibilityWrapRef.value;
  isResponsibilityTruncated.value = !!wrap && isOverflowing(wrap.querySelector(".rad__resp-text"));
}

onMounted(() => {
  measureTruncation();
  if (typeof ResizeObserver === "undefined") return;
  resizeObserver = new ResizeObserver(measureTruncation);
  [employeeRef.value, responsibilityWrapRef.value].forEach((el) => {
    if (el) resizeObserver.observe(el);
  });
});

onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect();
});

watch([displayName, () => props.jobTitle, resolvedResponsibilityName], () => {
  nextTick(measureTruncation);
});

function onBreadcrumbClick(breadcrumb) {
  emit("breadcrumb-click", breadcrumb);
}
</script>

<style scoped>
.as-coach-inspect-checklist-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 1px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  font-family:
    Inter,
    system-ui,
    -apple-system,
    "Segoe UI",
    Roboto,
    Arial,
    sans-serif;
}

.as-coach-inspect-checklist-card__header {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.as-coach-inspect-checklist-card__page-header {
  display: flex;
  flex-direction: column;
  gap: 1px;
  width: 100%;
  padding: 8px 0 0;
}

.as-coach-inspect-checklist-card__top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  width: 100%;
  min-height: 44px;
  padding: 8px 16px;
  box-sizing: border-box;
}

.as-coach-inspect-checklist-card__top-row--no-breadcrumbs {
  justify-content: flex-end;
}

.as-coach-inspect-checklist-card__breadcrumbs {
  flex: 1 1 0;
  min-width: 0;
}

.as-coach-inspect-checklist-card__assignments-badge {
  flex: 0 0 auto;
}

.as-coach-inspect-checklist-card__slide-out-header {
  display: flex;
  width: 100%;
  padding: 0 24px;
  box-sizing: border-box;
}

.as-coach-inspect-checklist-card__main-row {
  display: flex;
  flex: 1 1 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  width: 100%;
  min-width: 0;
  padding-top: 24px;
  box-sizing: border-box;
}

.as-coach-inspect-checklist-card__heading {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  gap: 16px;
  min-width: 0;
  max-width: 100%;
}

.as-coach-inspect-checklist-card__title {
  flex-shrink: 0;
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 30px;
  color: #101828;
  white-space: nowrap;
}

.as-coach-inspect-checklist-card__assignment-label {
  flex: 0 0 auto;
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 30px;
  color: #101828;
  white-space: nowrap;
}

.as-coach-inspect-checklist-card__employee-tooltip {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 480px;
}

.as-coach-inspect-checklist-card__employee {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  gap: 5px;
  height: 44px;
  min-width: 0;
  overflow: hidden;
}

.as-coach-inspect-checklist-card__avatar {
  flex-shrink: 0;
}

.as-coach-inspect-checklist-card__avatar :deep(.v-avatar) {
  width: 33px !important;
  height: 32px !important;
  min-width: 33px !important;
}

.as-coach-inspect-checklist-card__employee-name-wrap {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
}

.as-coach-inspect-checklist-card__employee-name {
  flex-shrink: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 32px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.as-coach-inspect-checklist-card__employee-title {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #475467;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.as-coach-inspect-checklist-card__responsibility-tooltip {
  flex: 1 1 0;
  min-width: 280px;
  max-width: 100%;
}

.as-coach-inspect-checklist-card__responsibility-dropdown-wrap {
  width: 100%;
  min-width: 0;
  height: 44px;
}

.as-coach-inspect-checklist-card__responsibility-dropdown {
  display: block;
  width: 100%;
  max-width: 100%;
  height: 44px;
}

.as-coach-inspect-checklist-card__body {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  padding: 8px 16px;
  box-sizing: border-box;
}

.as-coach-inspect-checklist-card__body :deep(.as-coach-checklist-inspection-table) {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
}

.as-coach-inspect-checklist-card__responsibility-dropdown-wrap :deep(.rad-menu),
.as-coach-inspect-checklist-card__responsibility-dropdown-wrap :deep(.v-menu) {
  display: block;
  width: 100%;
  max-width: 100%;
}

.as-coach-inspect-checklist-card__responsibility-dropdown-wrap :deep(.rad__trigger-btn) {
  width: 100% !important;
  max-width: 100% !important;
  height: 44px !important;
  min-height: 44px !important;
  box-sizing: border-box;
}

.as-coach-inspect-checklist-card__responsibility-dropdown-wrap :deep(.rad-card) {
  height: 44px;
  min-height: 44px;
  box-sizing: border-box;
}
</style>
